import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import './Login.css';

const Login = () => {
  const [searchParams] = useSearchParams();
  const params = useParams();
  const { user, login, checkAuth } = useAuth();
  const { tenantConfig } = useTenant();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const token = searchParams.get('token');
    const customerData = searchParams.get('customerData');
    
    // Only attempt external auth if we have both token and customerData
    if (token && customerData) {
      console.log('External auth parameters found, attempting authentication');
      const timestamp = Date.now(); // Use current timestamp
      handleExternalAuth(token, customerData, timestamp);
    }
  }, [searchParams]);

  const handleExternalAuth = async (token, customerData, timestamp) => {
    try {
      // Clear any existing session data first
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('shipEngineAPI');
      
      setLoading(true);
      setError('');
      console.log('Processing external authentication...');
      
      let parsedCustomerData;
      try {
        parsedCustomerData = JSON.parse(decodeURIComponent(customerData));
      } catch (e) {
        console.error('Error parsing customer data:', e);
        setError('Invalid customer data format');
        return;
      }

      const response = await api.post('/api/external-auth', {
        token,
        customerData: parsedCustomerData,
        timestamp
      });

      console.log('External auth response:', response.data);
      if (response.data.user) {
        console.log('External auth successful, setting user data');
        sessionStorage.setItem('user', JSON.stringify(response.data.user));
        if (response.data.user.shipEngineAPI) {
          sessionStorage.setItem('shipEngineAPI', response.data.user.shipEngineAPI);
        }
        navigate('/inventory');
      } else {
        // If no user data in response, ensure session is cleared
        sessionStorage.removeItem('user');
        sessionStorage.removeItem('shipEngineAPI');
        setError('Authentication failed - invalid response');
      }
    } catch (error) {
      console.error('External auth error:', error);
      // Ensure session is cleared on error
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('shipEngineAPI');
      setError(error.response?.data?.message || 'Authentication failed');
      if (error.response?.status === 401 && error.response?.data?.message?.includes('expired')) {
        setError('Your authentication session has expired. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const credentials = {
      username: formData.get('username'),
      password: formData.get('password')
    };

    try {
      setLoading(true);
      setError('');
      console.log('Attempting login...');
      await login(credentials);
    } catch (error) {
      console.error('Login error:', error);
      setError(error.response?.data?.message || 'Login failed');
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('shipEngineAPI');
    } finally {
      setLoading(false);
    }
  };

  // Redirect if already authenticated
  useEffect(() => {
    if (user) {
      const tenantPrefix = params.tenantPrefix;
      if (tenantPrefix) {
        navigate(`/${tenantPrefix}/inventory`);
      } else {
        navigate('/inventory');
      }
    }
  }, [user, navigate, params.tenantPrefix]);

  return (
    <div className="login-wrapper">
      <main className="login-content">
        <div 
          className="login-page-header" 
          style={{ 
            backgroundImage: "url('/splash.jpg')",
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div className="login-container">
            <div className="login-row">
              <div className="login-col">
                <div className="login-card login-fadeIn">
                  <div className="login-card-header">
                    <div className="login-gradient-primary">
                      <img 
                        src={tenantConfig.loginLogo} 
                        className="img-fluid px-3" 
                        alt="logo"
                        onError={(e) => {
                          e.target.src = '/bannernew.png'; // Fallback to default logo
                        }}
                      />
                      <h4 className="login-text-white login-text-center">
                        {/*{tenantConfig.tenantName}*/}
                      </h4>
                      <h4 className="login-text-white login-text-center">
                        Sign in
                      </h4>
                    </div>
                  </div>

                  <div className="login-card-body">
                    <form onSubmit={handleSubmit} className="login-text-center">
                      <div className="login-input-group">
                        <input
                          type="text"
                          name="username"
                          className="form-control"
                          placeholder="Username"
                          required
                        />
                      </div>

                      <div className="login-input-group">
                        <input
                          type="password"
                          name="password"
                          className="form-control"
                          placeholder="Password"
                          required
                        />
                      </div>

                      {error && (
                        <div className="login-text-center text-danger mb-3">
                          {error}
                        </div>
                      )}

                      <div className="login-text-center">
                        <button
                          type="submit"
                          className="login-btn"
                          disabled={loading}
                        >
                          {loading ? 'Signing in...' : 'Sign in'}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Login;
